'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetLeaderboardRow, useAuth, useGetLeaderboard, usePrefetchGetUser } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { userIdentityKey } from '@/components/profile/user-identity-key';
import { parseAvatarPreferences } from '@/components/social/avatar-preferences';
import { LeaderboardEmptyState } from '@/components/social/leaderboard/leaderboard-empty';
import { LeaderboardSkeleton } from '@/components/social/leaderboard/leaderboard-skeleton';
import { TabContent } from '@/components/social/leaderboard/tab-content';
import { useAnchorScroll } from '@/components/social/leaderboard/use-anchor-scroll';
import { mapLeaderboardEntries, readFiniteNumber, readFiniteNumericString } from '@/components/social/leaderboard/use-committed-leaderboard-entries';
import { SocialFeedList } from '@/components/social/shared/social-feed-list';
import type { CreatorEntry, CreatorSort } from '@/components/social/types';
import { DataTable } from '@/components/ui/data-table';
import { Panel } from '@/components/ui/panel';
import { D6_SCALE } from '@/config/solana';
import { useFormattedCurrency } from '@/lib/currency';
import { useAppRouter } from '@/lib/network/use-app-router';
import { LB_CREATORS_FIELDS, PORTFOLIO_USER_PROFILE_FIELDS } from '@/lib/sdk/projections';
import { SOCIAL_LEADERBOARD_LIST_PANEL_CLASS } from '@/lib/ui/layout';

// ---------------------------------------------------------------------------
// Creators tab — consumes the universal DataTable primitive (spec 093).
//
// Three columns: Top Market (sortable, default; ClickableCell with tooltip),
// Markets Created (sortable), Total Volume (sortable).
// ---------------------------------------------------------------------------

interface CreatorsTabProps {
  initialSort?: CreatorSort;
  onSortChange?: (sort: CreatorSort) => void;
  anchorUserId?: string | undefined;
  pulseTick?: number | undefined;
  /** Time window forwarded to the leaderboard hook (`1d`/`1w`/`1m`/`1y`/`lifetime`). */
  timeWindow?: string;
}
function rankForActiveSort(entry: CreatorEntry, sortBy: CreatorSort): number {
  switch (sortBy) {
    case 'topMarket':
      return entry.topMarketRank;
    case 'marketsCreated':
      return entry.marketsCreatedRank;
    case 'totalVolume':
      return entry.totalVolumeRank;
  }
}
function mapEntry(item: GetLeaderboardRow, sortBy: CreatorSort): CreatorEntry | null {
  const userField = (item.user ?? {}) as Record<string, unknown>;
  const providersRaw = Array.isArray(userField.connectedProviders) ? userField.connectedProviders as Array<Record<string, unknown>> : [];
  const topMarketPubkey = typeof item.topMarketPubkey === 'string' ? item.topMarketPubkey : '';
  const topMarketVolume = topMarketPubkey === '' ? '0' : readFiniteNumericString(item.topMarketVolume);
  const totalMarketsCreated = item.totalMarketsCreated === null ? 0 : readFiniteNumber(item.totalMarketsCreated);
  const totalVolume = readFiniteNumericString(item.creatorTotalVolume);
  const activeRank = readFiniteNumber(sortBy === 'topMarket' ? item.topMarketRank : sortBy === 'marketsCreated' ? item.marketsCreatedRank : item.totalVolumeRank);
  const topMarketRank = item.topMarketRank === null ? activeRank : readFiniteNumber(item.topMarketRank);
  const marketsCreatedRank = item.marketsCreatedRank === null ? activeRank : readFiniteNumber(item.marketsCreatedRank);
  const totalVolumeRank = item.totalVolumeRank === null ? activeRank : readFiniteNumber(item.totalVolumeRank);
  if (topMarketVolume === null || totalMarketsCreated === null || totalVolume === null || activeRank === null || topMarketRank === null || marketsCreatedRank === null || totalVolumeRank === null) {
    return null;
  }
  return {
    user: {
      userId: typeof userField.userId === 'string' ? userField.userId : null,
      walletAddress: typeof userField.walletAddress === 'string' ? userField.walletAddress : '',
      displayName: typeof userField.displayName === 'string' ? userField.displayName : null,
      handle: typeof userField.handle === 'string' ? userField.handle : null,
      avatarUrl: typeof userField.avatarUrl === 'string' ? userField.avatarUrl : null,
      avatarNsfw: userField.avatarNsfw === true,
      avatarPreferences: parseAvatarPreferences(userField.avatarPreferences),
      connectedProviders: providersRaw.filter((p): p is {
        provider: 'x' | 'google' | 'apple';
      } & Record<string, unknown> => p.provider === 'x' || p.provider === 'google' || p.provider === 'apple').map(p => ({
        provider: p.provider,
        username: typeof p.username === 'string' ? p.username : '',
        displayName: typeof p.displayName === 'string' ? p.displayName : null
      }))
    },
    topMarketPubkey,
    topMarketTitle: String(item.topMarketTitle ?? ''),
    topMarketVolume,
    totalMarketsCreated,
    totalVolume,
    topMarketRank,
    marketsCreatedRank,
    totalVolumeRank
  };
}
export function CreatorsTab(t0) {
  const $ = _c(70);
  const {
    initialSort,
    onSortChange,
    anchorUserId,
    pulseTick,
    timeWindow
  } = t0;
  const tCreators = useTranslations("social.creators");
  const tLb = useTranslations("social.leaderboard");
  const {
    user
  } = useAuth();
  const router = useAppRouter();
  const prefetchUser = usePrefetchGetUser();
  const {
    formatBalance
  } = useFormattedCurrency();
  const userId = user?.id ?? null;
  const [sortBy, setSortBy] = useState(initialSort ?? "totalVolume");
  const sortOrder = "desc" as const;
  let t1;
  if ($[0] !== onSortChange) {
    t1 = function handleSortChange(next) {
      setSortBy(next);
      if (onSortChange) {
        onSortChange(next);
      }
    };
    $[0] = onSortChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSortChange = t1;
  let t2;
  if ($[2] !== timeWindow) {
    t2 = timeWindow !== undefined ? {
      window: timeWindow
    } : {};
    $[2] = timeWindow;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [...LB_CREATORS_FIELDS];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== sortBy || $[6] !== t2) {
    t4 = {
      kind: "creators",
      sortBy,
      sortOrder: "desc",
      ...t2,
      fields: t3
    };
    $[5] = sortBy;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const leaderboardQuery = useGetLeaderboard(t4);
  const leaderboardStatus = leaderboardQuery.status;
  const leaderboardData = leaderboardQuery.rows;
  const isResolved = leaderboardStatus === "success" && leaderboardQuery.isFetched && !leaderboardQuery.isPlaceholderData;
  let isInitialLoad;
  let rawEntries;
  let t5;
  if ($[8] !== anchorUserId || $[9] !== isResolved || $[10] !== leaderboardData || $[11] !== sortBy) {
    rawEntries = mapLeaderboardEntries(leaderboardData, isResolved, mapEntry, sortBy);
    isInitialLoad = !isResolved && rawEntries.length === 0;
    t5 = anchorUserId !== undefined && rawEntries.some(entry => entry.user.userId === anchorUserId);
    $[8] = anchorUserId;
    $[9] = isResolved;
    $[10] = leaderboardData;
    $[11] = sortBy;
    $[12] = isInitialLoad;
    $[13] = rawEntries;
    $[14] = t5;
  } else {
    isInitialLoad = $[12];
    rawEntries = $[13];
    t5 = $[14];
  }
  const isAnchorLoaded = t5;
  const t6 = leaderboardQuery.hasNextPage === true;
  const t7 = leaderboardQuery.isFetchingNextPage === true;
  let t8;
  if ($[15] !== anchorUserId || $[16] !== isAnchorLoaded || $[17] !== leaderboardQuery.fetchNextPage || $[18] !== pulseTick || $[19] !== t6 || $[20] !== t7) {
    t8 = {
      pulseTick,
      anchorUserId,
      isAnchorLoaded,
      hasNextPage: t6,
      isFetchingNextPage: t7,
      fetchNextPage: leaderboardQuery.fetchNextPage
    };
    $[15] = anchorUserId;
    $[16] = isAnchorLoaded;
    $[17] = leaderboardQuery.fetchNextPage;
    $[18] = pulseTick;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const scrollTick = useAnchorScroll(t8);
  if (isInitialLoad) {
    let t9;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <TabContent><Panel surface="solid" inheritBackground={true} className={SOCIAL_LEADERBOARD_LIST_PANEL_CLASS} data-testid="social-leaderboard-panel"><LeaderboardSkeleton columnCount={4} /></Panel></TabContent>;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    return t9;
  }
  if (rawEntries.length === 0) {
    let t9;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <TabContent><Panel surface="solid" inheritBackground={true} className={SOCIAL_LEADERBOARD_LIST_PANEL_CLASS} data-testid="social-leaderboard-panel"><LeaderboardEmptyState tab="creators" /></Panel></TabContent>;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    return t9;
  }
  const t9 = leaderboardQuery.hasNextPage === true;
  const t10 = leaderboardQuery.isFetchingNextPage === true;
  const t11 = leaderboardQuery.fetchNextPage;
  const t12 = leaderboardQuery.isError;
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <DataTable.RankColumn />;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== tLb) {
    t14 = tLb("trader");
    $[25] = tLb;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t14) {
    t15 = <DataTable.IdentityColumn header={t14} />;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== tCreators) {
    t16 = tCreators("column_total_volume");
    $[29] = tCreators;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t16) {
    t17 = <DataTable.SortableColumn sortKey="totalVolume" header={t16} align="right" className="w-[26%] whitespace-nowrap" />;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== tCreators) {
    t18 = tCreators("column_markets_created");
    $[33] = tCreators;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t18) {
    t19 = <DataTable.SortableColumn sortKey="marketsCreated" header={t18} align="right" className="w-[16%] whitespace-nowrap" />;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== tCreators) {
    t20 = tCreators("column_top_market");
    $[37] = tCreators;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== tCreators) {
    t21 = tCreators("column_top_market_tooltip");
    $[39] = tCreators;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t20 || $[42] !== t21) {
    t22 = <DataTable.SortableColumn sortKey="topMarket" header={t20} headerTooltip={t21} align="right" className="w-[16%] whitespace-nowrap" />;
    $[41] = t20;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== t15 || $[45] !== t17 || $[46] !== t19 || $[47] !== t22) {
    t23 = <DataTable.Header>{t13}{t15}{t17}{t19}{t22}</DataTable.Header>;
    $[44] = t15;
    $[45] = t17;
    $[46] = t19;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== formatBalance || $[50] !== prefetchUser || $[51] !== router || $[52] !== sortBy || $[53] !== tCreators || $[54] !== tLb || $[55] !== userId) {
    t24 = <DataTable.Body>{entry_0 => {
        const rank = rankForActiveSort(entry_0, sortBy);
        const entryUserId = entry_0.user.userId ?? "";
        const isUser = userId !== null && entry_0.user.userId === userId;
        const navigate = function navigate() {
          if (entryUserId.length === 0) {
            return;
          }
          router.push(`/portfolio/${entryUserId}`);
        };
        const prefetch = function prefetch() {
          if (entryUserId.length === 0) {
            return;
          }
          prefetchUser({
            identifier: entryUserId,
            fields: [...PORTFOLIO_USER_PROFILE_FIELDS]
          });
        };
        const topMarketContent = entry_0.topMarketPubkey.length > 0 ? <DataTable.ClickableCell href={`/markets/${entry_0.topMarketPubkey}`} ariaLabel={tCreators("top_market_arrow_aria")} align="right" hideOnMobile={true}>{formatBalance(Number(entry_0.topMarketVolume) / D6_SCALE, "compactTight")}</DataTable.ClickableCell> : <DataTable.NumericCell value={0} format="compact" tone="muted" hideOnMobile={true} />;
        return <DataTable.Row key={userIdentityKey(entry_0.user, `anonymous-rank-${rank}`)} isCurrentUser={isUser} onClick={navigate} onMouseEnter={prefetch} onFocus={prefetch} onPointerDown={prefetch} ariaLabel={tLb("aria_view_profile")}><DataTable.RankCell value={rank} isCurrentUser={isUser} /><DataTable.IdentityCell user={entry_0.user} showYouBadge={isUser} /><DataTable.NumericCell value={Number(entry_0.totalVolume)} format="compact" /><DataTable.NumericCell value={entry_0.totalMarketsCreated} format="int" hideOnMobile={true} />{topMarketContent}</DataTable.Row>;
      }}</DataTable.Body>;
    $[49] = formatBalance;
    $[50] = prefetchUser;
    $[51] = router;
    $[52] = sortBy;
    $[53] = tCreators;
    $[54] = tLb;
    $[55] = userId;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== handleSortChange || $[58] !== rawEntries || $[59] !== scrollTick || $[60] !== sortBy || $[61] !== t23 || $[62] !== t24) {
    t25 = <DataTable panel="borderless" desktopRowChrome="feed" entries={rawEntries} sortBy={sortBy} sortOrder={sortOrder} onSortChange={handleSortChange} pulseTick={scrollTick}>{t23}{t24}</DataTable>;
    $[57] = handleSortChange;
    $[58] = rawEntries;
    $[59] = scrollTick;
    $[60] = sortBy;
    $[61] = t23;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== leaderboardQuery.fetchNextPage || $[65] !== leaderboardQuery.isError || $[66] !== t10 || $[67] !== t25 || $[68] !== t9) {
    t26 = <TabContent><Panel surface="solid" inheritBackground={true} className={SOCIAL_LEADERBOARD_LIST_PANEL_CLASS} data-testid="social-leaderboard-panel"><SocialFeedList hasNextPage={t9} isFetchingNextPage={t10} fetchNextPage={t11} isError={t12} data-testid="social-creators-scroll">{t25}</SocialFeedList></Panel></TabContent>;
    $[64] = leaderboardQuery.fetchNextPage;
    $[65] = leaderboardQuery.isError;
    $[66] = t10;
    $[67] = t25;
    $[68] = t9;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  return t26;
}